import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { FC, useEffect, useMemo, useState } from "react";
import { PsicologoEF } from "src/apis/eezyfamily";
import NoDataAlert from "src/pages/components/NoDataAlert";

const ROWS = 10;

type Props = {
  psicologos: PsicologoEF[];
  onEdit: (item: PsicologoEF) => void;
  onDelete: (id: number) => void;
};

const PsicologosTable: FC<Props> = ({
  psicologos,
  onEdit,
  onDelete,
}): JSX.Element => {
  const hasData = useMemo(
    () => Boolean(psicologos) && psicologos.length > 0,
    [psicologos]
  );
  const [entries, setEntries] = useState<PsicologoEF[]>(psicologos);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (hasData)
      setEntries(
        [...psicologos].sort((a, b) =>
          a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
        )
      );
  }, [hasData, psicologos]);

  const pageSlice = entries.slice(page * ROWS, page * ROWS + ROWS);

  if (!hasData) return <NoDataAlert />;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight={600}>Nome</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Localização</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Abordagem</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Status</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={600}>Ações</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageSlice.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.location ?? "—"}</TableCell>
                <TableCell>{item.approach ?? "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.is_active ? "Ativo" : "Inativo"}
                    color={item.is_active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => onEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => onDelete(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={entries.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={ROWS}
        rowsPerPageOptions={[ROWS]}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </>
  );
};

export default PsicologosTable;
